import isEqual from 'lodash/isEqual';
import type { BridgeContextInput } from './BridgeContextObject';

type BridgeContextFields = keyof BridgeContextInput;

/**
 * Per-key change record produced by `computeBridgeContextChanges`.
 *
 * Each known `BridgeContextInput` key maps to whether it changed under the configured rules.
 * `__all__` is `true` if any key changed. Future optimisations (e.g. listeners reacting only
 * to a specific key) can consume per-key data without re-walking the props.
 *
 * `extensionPayloadChanges` is populated whenever `extensionPayload` itself changed.
 * Each entry uses **reference equality** as a cheap pre-filter: `true` means the sub-key's
 * reference changed, which is a necessary but not sufficient condition for an actual value
 * change. Listeners that need value-level accuracy must add a secondary comparison (e.g.
 * `isEqual`) when the flag is `true`. The win is avoiding any comparison at all when the
 * flag is `false` — e.g. `config` stays `false` when only `isEditing` changes and `config`
 * keeps its reference.
 */
export type BridgeContextChanges = Record<BridgeContextFields, boolean> & {
	__all__: boolean;
	extensionPayloadChanges: Readonly<Record<string, boolean>>;
};

// These are standard fields in the BridgeContextChanges, they are not used to indicate any specific context field changes.
const DEFAULT_CONTEXT_CHANGES_PROPS = ['__all__', 'extensionPayloadChanges'];

/**
 * Keys excluded entirely from the change calculation. Products typically pass a
 * freshly-allocated `bridge` object on every render, so reference-comparing it would mark
 * every render as a context change and spuriously fire listeners.
 */
const IGNORED_KEYS: readonly BridgeContextFields[] = ['bridge', 'getContextToken'];

/**
 * Keys that are deep-compared (via `lodash.isEqual`) instead of reference-compared. Products
 * commonly re-allocate these objects on every render with structurally identical content
 * (e.g. `coreData = { cloudId, workspaceId, ... }`); a simple `!==` would spuriously flag
 * them as changed.
 *
 * NOTE: values referenced by these keys must be plain JSON-shaped objects (no class instances,
 * no Apollo client, no Relay environment, no React refs). `lodash.isEqual` will recurse into
 * anything you put here, including library internals — keep these shapes simple.
 */
const STRUCTURAL_KEYS: readonly BridgeContextFields[] = ['coreData', 'extension', 'contextIds'];

/**
 * Computes a per-key change record between `prev` and `next` under the configured rules:
 *
 *   - Ignored keys (e.g. `bridge`) are always recorded as `false`.
 *   - Structural keys (e.g. `coreData`, `extension`) are compared deeply.
 *   - Everything else uses reference equality.
 *
 * Walks every union key (no short-circuit) so the returned map is fully populated and
 * available for downstream optimisations such as per-key listener notification.
 */
export const computeBridgeContextChanges = (
	prev: BridgeContextInput,
	next: BridgeContextInput,
): BridgeContextChanges => {
	const allKeys = new Set<BridgeContextFields>([
		...(Object.keys(prev) as BridgeContextFields[]),
		...(Object.keys(next) as BridgeContextFields[]),
	]);

	const changes = { __all__: false, extensionPayloadChanges: {} } as BridgeContextChanges;
	for (const key of allKeys) {
		if (IGNORED_KEYS.includes(key)) {
			changes[key] = false;
			continue;
		}
		const prevValue = prev[key];
		const nextValue = next[key];
		const changed = STRUCTURAL_KEYS.includes(key)
			? !isEqual(prevValue, nextValue)
			: prevValue !== nextValue;
		changes[key] = changed;
		if (changed) {
			changes.__all__ = true;
		}
	}

	// When extensionPayload reference changed, record per-top-level-key reference equality
	// as a cheap pre-filter signal for listeners.
	//
	// IMPORTANT: reference equality only — NOT value equality. A sub-key flagged `true` means
	// its reference changed, not necessarily that its serialised value changed. Listeners that
	// care about actual value equality must perform their own secondary comparison (e.g.
	// `isEqual`) before acting.
	//
	// The benefit is still real: when only `isEditing` changes and `config` keeps its reference,
	// `extensionPayloadChanges.config` is `false` and the listener short-circuits with zero
	// deep comparisons.
	if (changes.extensionPayload) {
		const prevPayload = prev.extensionPayload ?? {};
		const nextPayload = next.extensionPayload ?? {};
		const subKeys = new Set([...Object.keys(prevPayload), ...Object.keys(nextPayload)]);
		const subChanges: Record<string, boolean> = {};
		for (const k of subKeys) {
			subChanges[k] =
				(prevPayload as Record<string, unknown>)[k] !== (nextPayload as Record<string, unknown>)[k];
		}
		changes.extensionPayloadChanges = subChanges;
	}

	return changes;
};

/**
 * Returns the list of `BridgeContextInput` field names that changed, excluding the
 * meta-only entries (`__all__`, `extensionPayloadChanges`) that exist on
 * `BridgeContextChanges` but do not correspond to any individual context field.
 *
 * Use this when you need a plain list of changed keys — e.g. for console logging or
 * passing to `diffProps` — rather than the full change record.
 */
export const getBaseChangedFields = (changes: BridgeContextChanges): BridgeContextFields[] => {
	return Object.keys(changes).filter(
		(k) => {
			const key = k as BridgeContextFields;
			return !DEFAULT_CONTEXT_CHANGES_PROPS.includes(key) && changes[key] === true;
		}
	) as BridgeContextFields[];
};
